export const POSTHOG_EVENT_CONTRACT_VERSION = 1

export type PostHogEventClassification =
  | 'customer_operations'
  | 'lifecycle'
  | 'product_usage'
  | 'system'

interface PostHogEventFamilyDefinition {
  classification: PostHogEventClassification
  description: string
  prefix: string
}

export const POSTHOG_EVENT_FAMILY_REGISTRY = [
  {
    prefix: 'mcp.',
    description: 'External MCP discovery and tool-call outcomes.',
    classification: 'product_usage',
  },
  {
    prefix: 'integration.',
    description: 'Connected-app lifecycle and synchronization outcomes.',
    classification: 'customer_operations',
  },
  {
    prefix: 'webhook.',
    description: 'Inbound provider webhook processing outcomes.',
    classification: 'customer_operations',
  },
  {
    prefix: 'twilio.',
    description: 'Voice and messaging provider lifecycle outcomes.',
    classification: 'customer_operations',
  },
  {
    prefix: 'task_',
    description: 'Task product lifecycle events.',
    classification: 'product_usage',
  },
  {
    prefix: 'customerio.',
    description: 'Customer lifecycle delivery events.',
    classification: 'lifecycle',
  },
  {
    prefix: 'product.',
    description:
      'Server-canonical core product metrics consumed by the customer-health rollup and its event-liveness check (INT-313).',
    classification: 'product_usage',
  },
] as const satisfies readonly PostHogEventFamilyDefinition[]

/**
 * The single source of truth for the core-metric event names. The gtm rollup
 * and the customer-health event-liveness check consume exactly these names;
 * renaming one here without updating both is what the contract tests exist to
 * catch. Twin registry: trellis-api/services/analytics/posthog_contract.py.
 */
export const PRODUCT_METRIC_EVENTS = [
  { name: 'product.task_created', requiredProperties: ['creator_type'] },
  { name: 'product.task_completed', requiredProperties: ['creator_type'] },
  { name: 'product.workflow_run', requiredProperties: ['status'] },
  { name: 'product.rf_outcome', requiredProperties: ['reply_id', 'kind', 'reply_required'] },
  { name: 'product.message_processed', requiredProperties: ['direction', 'channel'] },
] as const satisfies readonly { name: string; requiredProperties: readonly string[] }[]

const CANONICAL_EVENT_NAME = /^(?:[a-z][a-z0-9_]*)(?:\.[a-z][a-z0-9_]*)*$/
const URL_PROPERTY_KEY_RE = /(^|_)(url|uri|href|referrer|path|pathname)$/
const URL_SUFFIX_RE = /[?#]/
const PERFORMANCE_ENTRY_URL_RE = /^(?:https?:\/\/|\/)/
const UUID_PATH_SEGMENT_RE = /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i
const UUID_VALUE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const TOKEN_PATH_SEGMENT_RE = /^[A-Za-z0-9_-]{20,}$/
const EXCEPTION_IDENTIFIER_RE = /^[A-Za-z_$][\w.$]{0,127}$/
const EXCEPTION_LEVELS = new Set(['fatal', 'error', 'warning', 'log', 'info', 'debug'])
const EXCEPTION_PLATFORMS = new Set(['web:javascript', 'node:javascript', 'hermes'])
const EXCEPTION_NUMBER_FIELDS = new Set(['lineno', 'colno', 'exception_id', 'parent_id'])
const EXCEPTION_BOOLEAN_FIELDS = new Set(['handled', 'synthetic', 'in_app'])
const EXCEPTION_IDENTIFIER_FIELDS = new Set(['type', 'source', 'function'])
const REDACTED = '[redacted]'
const SENSITIVE_KEYS = [
  /(^|_)email$/,
  /(^|_)phone$/,
  /(^|_)access_code$/,
  /(^|_)auth(_|$)/,
  /(^|_)cookie(s)?$/,
  /(^|_)token$/,
  /(^|_)secret$/,
  /(^|_)password$/,
  /(^|_)(api_key|authorization|body|headers?|transcript|guest_name|user_text)$/,
  /(^|_)(error|exception)(_|$)/,
  /(^|_)(stack|stacktrace|traceback|cause)(_|$)/,
  /(^|_)message(_text|_body)?$/,
]
const SENSITIVE_VALUES = [/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i, /\bBearer\s+\S+/i]
const TOKEN_ROUTE_PREFIXES = new Set([
  'claim',
  'forms',
  'invite',
  'manual',
  'owner-portal',
  'task',
  'u',
  'visit',
  'widget',
])
const API_TOKEN_ROUTE_PREFIXES = new Set([
  'api/invites',
  'api/manual',
  'api/public/website-widgets',
  'api/v1/public/forms',
  'api/v1/public/owner-portal',
  'api/v1/public/shared-claim',
  'api/v1/public/shared-task',
  'api/v1/public/shared-visit',
])

export function requireCanonicalPostHogEventName(event: string): void {
  if (!CANONICAL_EVENT_NAME.test(event)) {
    throw new Error(`PostHog event name is not canonical: ${event}`)
  }
}

export function requireCanonicalPostHogWorkspaceIdentity(
  workspaceId: string,
  distinctId?: string | null
): void {
  if (!workspaceId.trim() || workspaceId !== workspaceId.trim()) {
    throw new Error('PostHog workspace identity must be non-empty and canonical')
  }
  if (distinctId?.startsWith('workspace:') && distinctId !== `workspace:${workspaceId}`) {
    throw new Error('PostHog synthetic identity must match its workspace')
  }
}

export function classifyPostHogEvent(event: string): PostHogEventClassification {
  return (
    POSTHOG_EVENT_FAMILY_REGISTRY.find(({ prefix }) => event.startsWith(prefix))?.classification ??
    'product_usage'
  )
}

export function redactPostHogProperties(
  properties: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [key, redactRootValue(key, value)])
  )
}

function redactObject(properties: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [key, redactValue(key, value)])
  )
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function redactRootValue(key: string, value: unknown): unknown {
  // SDK exception properties must retain their schema for error tracking to ingest them.
  // Only root properties get this treatment; arbitrary nested exception payloads stay private.
  switch (key) {
    case '$exception_list':
      return redactExceptionList(value)
    case '$exception_type':
      return redactExceptionIdentifier(value)
    case '$exception_level':
      return typeof value === 'string' && EXCEPTION_LEVELS.has(value) ? value : 'error'
    case '$exception_capture_enabled_server_side':
      return typeof value === 'boolean' ? value : REDACTED
    default:
      return redactValue(key, value)
  }
}

function redactValue(key: string, value: unknown): unknown {
  const normalizedKey = normalizePropertyKey(key)
  if (SENSITIVE_KEYS.some((pattern) => pattern.test(normalizedKey))) {
    return REDACTED
  }
  if (typeof value === 'string') {
    if (SENSITIVE_VALUES.some((pattern) => pattern.test(value)) || looksLikePhoneNumber(value)) {
      return REDACTED
    }
    if (isUrlKey(normalizedKey)) {
      return sanitizePostHogUrl(value)
    }
    return value
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactValue(key, item))
  }
  if (typeof PerformanceEntry !== 'undefined' && value instanceof PerformanceEntry) {
    const entry = value.toJSON()
    if (typeof entry.name === 'string' && PERFORMANCE_ENTRY_URL_RE.test(entry.name)) {
      entry.name = sanitizePostHogUrl(entry.name)
    }
    return redactObject(entry)
  }
  if (isPlainObject(value)) {
    return redactObject(value)
  }
  return value
}

function redactExceptionIdentifier(value: unknown): string {
  return typeof value === 'string' && EXCEPTION_IDENTIFIER_RE.test(value) ? value : REDACTED
}

function redactExceptionList(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value.filter(isPlainObject).map((exception) => {
    const sanitized: Record<string, unknown> = { value: REDACTED }
    if (exception.type !== undefined) {
      sanitized.type = redactExceptionIdentifier(exception.type)
    }
    if (isPlainObject(exception.mechanism)) {
      sanitized.mechanism = redactExceptionMetadata(exception.mechanism)
    }
    if (isPlainObject(exception.stacktrace)) {
      sanitized.stacktrace = {
        type: 'raw',
        frames: Array.isArray(exception.stacktrace.frames)
          ? exception.stacktrace.frames
              .filter(
                (frame): frame is Record<string, unknown> =>
                  isPlainObject(frame) &&
                  typeof frame.platform === 'string' &&
                  EXCEPTION_PLATFORMS.has(frame.platform)
              )
              .map(redactExceptionMetadata)
          : [],
      }
    }
    return sanitized
  })
}

function redactExceptionMetadata(properties: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(properties)) {
    if (
      EXCEPTION_NUMBER_FIELDS.has(key) &&
      typeof value === 'number' &&
      Number.isSafeInteger(value) &&
      value >= 0
    ) {
      sanitized[key] = value
    } else if (EXCEPTION_BOOLEAN_FIELDS.has(key) && typeof value === 'boolean') {
      sanitized[key] = value
    } else if (EXCEPTION_IDENTIFIER_FIELDS.has(key)) {
      sanitized[key] = redactExceptionIdentifier(value)
    } else if (key === 'platform' && typeof value === 'string' && EXCEPTION_PLATFORMS.has(value)) {
      sanitized[key] = value
    } else if ((key === 'filename' || key === 'abs_path') && typeof value === 'string') {
      sanitized[key] = redactValue('url', value)
    } else if (key === 'chunk_id' && typeof value === 'string' && UUID_VALUE_RE.test(value)) {
      sanitized[key] = value
    }
  }
  return sanitized
}

function normalizePropertyKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()
}

function isUrlKey(key: string): boolean {
  return URL_PROPERTY_KEY_RE.test(key)
}

function sanitizePostHogUrl(value: string): string {
  try {
    if (value.startsWith('/')) {
      return scrubSensitivePath(value.split(URL_SUFFIX_RE, 1)[0] || '/')
    }
    const parsed = new URL(value)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return REDACTED
    }
    return `${parsed.origin}${scrubSensitivePath(parsed.pathname)}`
  } catch {
    return REDACTED
  }
}

function scrubSensitivePath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) {
    return '/'
  }
  const scrubbed = segments.map((segment, index) => {
    if (
      isSensitiveTokenSegment(segments, index) ||
      UUID_PATH_SEGMENT_RE.test(segment) ||
      TOKEN_PATH_SEGMENT_RE.test(segment)
    ) {
      return ':redacted'
    }
    return segment
  })
  return `/${scrubbed.join('/')}`
}

function isSensitiveTokenSegment(segments: string[], index: number): boolean {
  if (index === 1 && TOKEN_ROUTE_PREFIXES.has(segments[0])) {
    return true
  }
  if (index === 2 && (segments[1] === 'file' || segments[1] === 'u')) {
    return true
  }

  const prefix = segments.slice(0, index).join('/')
  if (!API_TOKEN_ROUTE_PREFIXES.has(prefix)) {
    return false
  }
  return prefix !== 'api/manual' || segments[index] !== 'property'
}

function looksLikePhoneNumber(value: string): boolean {
  // UUIDs can contain several adjacent digit groups, but are correlation IDs, not phone numbers.
  if (UUID_VALUE_RE.test(value)) {
    return false
  }
  const candidates = value.match(/\+?[\d(][\d\s().-]{7,}\d/g) ?? []
  return candidates.some((candidate) => {
    const digits = candidate.replace(/\D/g, '').length
    const separators = candidate.replace(/[\d+]/g, '').length
    return digits >= 9 && digits <= 15 && (candidate.startsWith('+') || separators >= 2)
  })
}
